import {
  and,
  collection,
  doc,
  getDocs,
  onSnapshot,
  or,
  orderBy,
  query,
  QueryDocumentSnapshot,
  QuerySnapshot,
  DocumentData,
  startAfter,
  where,
  limit,
} from 'firebase/firestore';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useAuthContext } from '@hooks/useAuthContext';
import { firestore } from '@/utils';
import { Notification } from '../entities/notification';
import { Counts } from '../entities/count';

const processNotifications = (notificationsData: DocumentData[]): Notification[] =>
  notificationsData.map((notification) => {
    const { id, topic, type, read, metadata, event_name, created_at } = notification || {};

    return new Notification({
      id,
      topic,
      type,
      read,
      metadata,
      timestamp: created_at,
      eventName: event_name,
    });
  });

export const useUserNotificationsQuery = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [totalNotifications, setTotalNotifications] = useState<number>(0);
  const [unreadNotifications, setUnreadNotifications] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lastVisibleDoc, setLastVisibleDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(
    null
  );
  const unsubscribeRef = useRef<() => void>(() => {});
  const { user } = useAuthContext();
  const branchSelected = useMemo(() => user?.branchSelected?.id, [user]);

  const createNotificationsQuery = (
    id: string,
    lastVisible?: QueryDocumentSnapshot<DocumentData, DocumentData>,
    pageSize: number = 10,
    applyLimit: boolean = true
  ) => {
    if (!firestore || !branchSelected) {
      throw new Error('Firestore is not initialized');
    }
    const usersRef = doc(collection(firestore, 'users'), id);
    const notificationsRef = collection(usersRef, 'notifications');

    const filterConstraints = [
      or(where('channel', '==', 'DITTO'), where('channel', '==', 'ALL')),
      ...(user?.companySelected?.id ? [where('company_id', '==', user.companySelected.id)] : []),
      ...(branchSelected
        ? [
            or(
              where('branch_id', '==', branchSelected),
              where('branch_id', '==', ''),
              where('branch_id', '==', null)
            ),
          ]
        : []),
    ];

    const baseQuery = query(notificationsRef, and(...filterConstraints));
    const constraints = [
      orderBy('created_at', 'desc'),
      ...(lastVisible ? [startAfter(lastVisible)] : []),
      ...(applyLimit && pageSize ? [limit(pageSize)] : []),
    ];

    return query(baseQuery, ...constraints);
  };

  const mapSnapshotToNotifications = (
    snapshot: QuerySnapshot<DocumentData, DocumentData>
  ): DocumentData[] =>
    snapshot?.docs?.map((document: QueryDocumentSnapshot<DocumentData, DocumentData>) => ({
      ...document.data(),
      id: document.id,
    })) ?? [];

  const handleCountsUpdate = (counts: Counts) => {
    setTotalNotifications(counts.getTotal());
    setUnreadNotifications(counts.getUnread());
  };

  const handleNotificationsUpdate = (notificationsData: DocumentData[]) => {
    const newNotifications = processNotifications(notificationsData);
    setNotifications(newNotifications);
  };

  const fetchNotifications = useCallback(
    async (id: string, pageSize?: number) => {
      setIsLoading(true);
      try {
        const notificationsQuery = createNotificationsQuery(id, undefined, pageSize, true);
        const snapshot = await getDocs(notificationsQuery);
        const notificationsData = mapSnapshotToNotifications(snapshot);
        const userNotifications = processNotifications(notificationsData);
        setNotifications(userNotifications);
        if (snapshot.docs.length > 0) {
          setLastVisibleDoc(snapshot.docs[snapshot.docs.length - 1]);
        }
      } catch (error) {
        console.error('Error fetching notifications: ', error);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [branchSelected, user?.id]
  );

  const fetchNextPage = useCallback(
    async (id: string, pageSize: number = 10) => {
      if (!lastVisibleDoc) return; // No hay más documentos a cargar

      setIsLoading(true);
      try {
        const notificationsQuery = createNotificationsQuery(id, lastVisibleDoc, pageSize, true);
        const snapshot = await getDocs(notificationsQuery);
        const notificationsData = mapSnapshotToNotifications(snapshot);
        const userNotifications = processNotifications(notificationsData);

        // Acumulamos los nuevos resultados a los que ya tenemos
        setNotifications((prev) => [...prev, ...userNotifications]);

        // Actualizamos el último documento visible
        if (snapshot.docs.length > 0) {
          setLastVisibleDoc(snapshot.docs[snapshot.docs.length - 1]);
        }
      } catch (error) {
        console.error('Error fetching next page of notifications: ', error);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastVisibleDoc, branchSelected, user?.id]
  );

  const onRealtimeCounts = (id: string, pageSize: number = 10): (() => void) => {
    const totalNotificationsQuery = createNotificationsQuery(id, undefined, undefined, false);
    const unreadNotificationsQuery = query(totalNotificationsQuery, where('read', '==', false));
    const counts = new Counts(0, 0);
    const totalNotificationsListener = onSnapshot(
      totalNotificationsQuery,
      (snapshot) => {
        counts.setTotal(snapshot.size);
        handleCountsUpdate(counts);
      },
      (error) => {
        console.error('Error listening for total notifications: ', error);
        return () => {};
      }
    );
    const unreadNotificationsListener = onSnapshot(
      unreadNotificationsQuery,
      (snapshot) => {
        counts.setUnread(snapshot.size);
        handleCountsUpdate(counts);
      },
      (error) => {
        console.error('Error listening for unread notifications: ', error);
        return () => {};
      }
    );
    return () => {
      totalNotificationsListener();
      unreadNotificationsListener();
    };
  };

  const onRealtimeNotificationsUpdates = (id: string, pageSize: number = 10): (() => void) => {
    try {
      const notificationsQuery = createNotificationsQuery(id, undefined, pageSize, true);
      return onSnapshot(
        notificationsQuery,
        (snapshot) => {
          try {
            const updatedNotifications = mapSnapshotToNotifications(snapshot);
            handleNotificationsUpdate(updatedNotifications);
          } catch (snapshotError) {
            console.error('Error processing Firestore snapshot: ', snapshotError);
          }
        },
        (error) => {
          console.error('Error with Firestore snapshot listener: ', error);
        }
      );
    } catch (error) {
      console.error('Error setting up Firestore snapshot listener: ', error);
      return () => {};
    }
  };

  const startRealtimeUpdates = useCallback(
    (id: string) => {
      try {
        if (firestore) {
          const countUnsubscribe = onRealtimeCounts(`${id}`);
          const notificationUnsubscribe = onRealtimeNotificationsUpdates(`${id}`);
          const combinedUnsubscribe = () => {
            countUnsubscribe();
            notificationUnsubscribe();
          };
          unsubscribeRef.current = combinedUnsubscribe;
        }
      } catch (error) {
        console.error('Error starting realtime updates: ', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [branchSelected, user?.id]
  );

  const stopRealtimeUpdates = useCallback(() => {
    unsubscribeRef.current();
  }, []);

  return {
    notifications,
    totalNotifications,
    unreadNotifications,
    fetchNotifications,
    fetchNextPage,
    startRealtimeUpdates,
    stopRealtimeUpdates,
    isLoading,
  };
};
